import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

interface DiagnosisTemplate {
  id: string
  name: string
  category: string
  organ_system: string
  template_type: 'standard' | 'specialized' | 'custom'
  description: string
  fields: TemplateField[]
  usage_count: number
  is_active: boolean
  created_by: string
  created_at: string
  last_updated: string
}

interface TemplateField {
  id: string
  name: string
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'number'
  label: string
  placeholder?: string
  required: boolean
  options?: string[]
  default_value?: string
  order: number
}

const DiagnosisTemplates: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<DiagnosisTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<DiagnosisTemplate | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [, setShowEditModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const [formData, setFormData] = useState<{
    name: string
    category: string
    organ_system: string
    template_type: 'standard' | 'specialized' | 'custom'
    description: string
    fields: TemplateField[]
  }>({
    name: '',
    category: '',
    organ_system: '',
    template_type: 'standard',
    description: '',
    fields: []
  })

  const categories = [
    'Surgical Pathology',
    'Cytopathology',
    'Dermatopathology',
    'Neuropathology',
    'Cardiovascular',
    'Gastrointestinal',
    'Genitourinary',
    'Pulmonary',
    'Breast',
    'Bone & Soft Tissue',
    'Other'
  ]

  // Built-in cytology template
  const cytologyTemplate: DiagnosisTemplate = {
    id: 'cytology-bethesda-2014',
    name: t('cytology.title'),
    category: 'Cytopathology',
    organ_system: 'Cervical',
    template_type: 'specialized',
    description: t('cytology.bethesdaTitle'),
    fields: [],
    usage_count: 0,
    is_active: true,
    created_by: 'System',
    created_at: new Date().toISOString(),
    last_updated: new Date().toISOString()
  }

  const fetchTemplates = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/diagnosis/templates/')
      if (response.ok) {
        const data = await response.json()
        // Add built-in cytology template at the beginning
        setTemplates([cytologyTemplate, ...(data.results || data)])
      } else {
        // If API fails, still show cytology template
        setTemplates([cytologyTemplate])
        toast.error('Failed to fetch templates')
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
      // If API fails, still show cytology template
      setTemplates([cytologyTemplate])
      toast.error('Error fetching templates')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const handleCreateTemplate = async () => {
    try {
      const response = await fetch('/api/v1/diagnosis/templates/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Template created successfully')
        setShowCreateModal(false)
        resetForm()
        fetchTemplates()
      } else {
        const errorData = await response.json()
        toast.error(errorData.detail || 'Failed to create template')
      }
    } catch (error) {
      console.error('Error creating template:', error)
      toast.error('Error creating template')
    }
  }


  const handleToggleActive = async (templateId: string, isActive: boolean) => {
    try {
      const response = await fetch(`/api/v1/diagnosis/templates/${templateId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !isActive })
      })

      if (response.ok) {
        toast.success(`Template ${!isActive ? 'activated' : 'deactivated'} successfully`)
        fetchTemplates()
      } else {
        toast.error('Failed to update template status')
      }
    } catch (error) {
      console.error('Error updating template status:', error)
      toast.error('Error updating template status')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      organ_system: '',
      template_type: 'standard',
      description: '',
      fields: []
    })
  }

  const openEditModal = (template: DiagnosisTemplate) => {
    setSelectedTemplate(template)
    setFormData({
      name: template.name,
      category: template.category,
      organ_system: template.organ_system,
      template_type: template.template_type,
      description: template.description,
      fields: template.fields
    })
    setShowEditModal(true)
  }

  const addField = () => {
    const newField: TemplateField = {
      id: `field_${Date.now()}`,
      name: '',
      type: 'text',
      label: '',
      required: false,
      order: formData.fields.length + 1
    }
    setFormData({
      ...formData,
      fields: [...formData.fields, newField]
    })
  }

  const updateField = (index: number, field: Partial<TemplateField>) => {
    const updatedFields = [...formData.fields]
    updatedFields[index] = { ...updatedFields[index], ...field }
    setFormData({ ...formData, fields: updatedFields })
  }

  const removeField = (index: number) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index)
    setFormData({ ...formData, fields: updatedFields })
  }

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      standard: 'bg-blue-100 text-blue-800',
      specialized: 'bg-purple-100 text-purple-800',
      custom: 'bg-green-100 text-green-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.organ_system.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter
    const matchesType = typeFilter === 'all' || template.template_type === typeFilter

    return matchesSearch && matchesCategory && matchesType
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Biểu Mẫu Phiếu Kết Quả</h1>
          <p className="text-gray-600 mt-1">Tạo và quản lý các biểu mẫu phiếu kết quả chuẩn hóa (Tế bào học, Mô bệnh học,...)</p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowCreateModal(true)
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
        >
          <span className="mr-2">+</span>
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="standard">Standard</option>
              <option value="specialized">Specialized</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border">
            <p className="text-gray-500">No templates found</p>
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-600">{template.category}</p>
                  <p className="text-xs text-gray-500">{template.organ_system}</p>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(template.template_type)}`}>
                    {template.template_type.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    template.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {template.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {template.description}
              </p>

              <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                <span>{template.fields.length} fields</span>
                <span>Used {template.usage_count} times</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  {template.id === 'cytology-bethesda-2014' ? (
                    <>
                      <button
                        onClick={() => navigate('/pathology/reports/cytology')}
                        className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 text-sm font-medium"
                      >
                        Open Form
                      </button>
                      <button
                        onClick={() => navigate('/pathology/reports/cytology/form-builder')}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium border border-blue-600 px-3 py-1.5 rounded-md"
                      >
                        ⚙️ Edit Template
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setSelectedTemplate(template)
                          setShowPreviewModal(true)
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => openEditModal(template)}
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
                {template.id !== 'cytology-bethesda-2014' && (
                  <button
                    onClick={() => handleToggleActive(template.id, template.is_active)}
                    className={`text-sm ${
                      template.is_active
                        ? 'text-red-600 hover:text-red-800'
                        : 'text-green-600 hover:text-green-800'
                    }`}
                  >
                    {template.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Create New Template</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Organ System</label>
                <input
                  type="text"
                  value={formData.organ_system}
                  onChange={(e) => setFormData({...formData, organ_system: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Type</label>
                <select
                  value={formData.template_type}
                  onChange={(e) => setFormData({...formData, template_type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="standard">Standard</option>
                  <option value="specialized">Specialized</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Template Fields */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium text-gray-900">Template Fields</h4>
                <button
                  onClick={addField}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  + Add Field
                </button>
              </div>

              <div className="space-y-4">
                {formData.fields.map((field, index) => (
                  <div key={field.id} className="border rounded-md p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateField(index, { name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                        <select
                          value={field.type}
                          onChange={(e) => updateField(index, { type: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="text">Text</option>
                          <option value="textarea">Textarea</option>
                          <option value="select">Select</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="number">Number</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={(e) => updateField(index, { required: e.target.checked })}
                          className="mr-2"
                        />
                        Required
                      </label>
                      <button
                        onClick={() => removeField(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                Template Preview - {selectedTemplate.name}
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {selectedTemplate.fields.map((field) => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'text' && (
                    <input
                      type="text"
                      placeholder={field.placeholder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      disabled
                    />
                  )}
                  {field.type === 'textarea' && (
                    <textarea
                      placeholder={field.placeholder}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      disabled
                    />
                  )}
                  {field.type === 'select' && (
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md" disabled>
                      <option>Select an option</option>
                      {field.options?.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DiagnosisTemplates